/**
 * GalaxyPanel.tsx — W66.X6: Command View — Flowchart / Account Path
 *
 * Desktop/Tablet: Mounts FlowchartWall inside a fixed-aspect constrained container
 *   with CSS transform scale to fit the panel. FlowchartWall receives accountId
 *   for account-path highlighting. A "Open full Galaxy" deep-link navigates to
 *   /galaxy/:accountId via the W49 GalaxyEntryButton pattern.
 *
 * Mobile: Static GalaxyMiniThumbnail (large) + "Tap to open Galaxy" button.
 *   FlowchartWall is NOT mounted on mobile to avoid performance hit.
 *   The full Galaxy mount happens lazily only on accordion expand (controlled
 *   by the parent CommandViewLayout).
 *
 * W45.3: SelectedCpContext is consumed here so GalaxyPanel is CP-highlight-aware.
 * W45.3.1: FlowchartWall now accepts onCpClick / selectedCp props. Clicking any
 * Q-diamond in the 3D scene broadcasts the CP number via useSelectedCp, syncing
 * Helix and Tower.
 *
 * W66.X6: Description card added above the flowchart per Bryan K. Short money-shot spec.
 *
 * Brand: No exclamation points. No emojis. M-PACT hyphenated.
 * "Checkpoint" not "Gate". AE name is Demo AE 1 — never Geoff.
 * 5-color palette: #6B7280 gray, #DC2626 red, #EA580C orange, #EAB308 yellow, #16A34A green.
 */

import React, { Suspense, lazy, useRef, useEffect, useState } from "react";
import { useLocation } from "wouter";
import GalaxyMiniThumbnail from "@/components/galaxy/GalaxyMiniThumbnail";
import { useSelectedCp } from "@/hooks/useSelectedCp";
import { HELIX_CHECKPOINTS } from "@/lib/helix-checkpoints";

// Code-split the heavy 3D scene — identical pattern to Galaxy.tsx
const FlowchartWall = lazy(() => import("@/components/galaxy/FlowchartWall"));

interface GalaxyPanelProps {
  accountId: string;
  accountName?: string;
  /** When true, do not mount FlowchartWall (mobile collapsed accordion). */
  suppressMount?: boolean;
}

// ─── Description card ────────────────────────────────────────────────────────

function GalaxyDescriptionCard() {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: 12,
        flexShrink: 0,
        margin: "0 10px 0 10px",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#111827",
          marginBottom: 4,
        }}
      >
        Account Path Flowchart
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#6B7280",
          lineHeight: 1.55,
        }}
      >
        The decision tree the deal has traveled and the path remaining. Diamonds
        are answered questions; rectangles are homework still owed. The lit path
        shows what is resolved; the dim path shows what remains. Tap any diamond
        to jump to that checkpoint.
      </div>
    </div>
  );
}

function GalaxyLoadingSpinner() {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a0a14 0%, #0d0d1a 40%, #1a1a2e 100%)",
        fontSize: 12,
        color: "rgba(148,163,184,0.5)",
        letterSpacing: "0.04em",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      Loading flowchart…
    </div>
  );
}

export default function GalaxyPanel({
  accountId,
  accountName,
  suppressMount = false,
}: GalaxyPanelProps) {
  const [, setLoc] = useLocation();
  // W45.3: cross-panel CP highlight state
  const { selectedCp, setSelectedCp } = useSelectedCp();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const [containerH, setContainerH] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) {
        setContainerW(e.contentRect.width);
        setContainerH(e.contentRect.height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function handleOpenGalaxy(e: React.MouseEvent) {
    e.preventDefault();
    setLoc(`/galaxy/${accountId}`);
  }

  // Mobile fallback: show thumbnail + open button
  if (suppressMount) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          minHeight: 200,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          background: "linear-gradient(135deg, #0a0a14 0%, #0d0d1a 50%, #1a1a2e 100%)",
        }}
      >
        <GalaxyMiniThumbnail size={96} style={{ borderRadius: 12, opacity: 0.85 }} />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 12, color: "rgba(148,163,184,0.7)", marginBottom: 10, letterSpacing: "0.04em" }}>
            Full flowchart available in Galaxy view
          </div>
          <button
            onClick={handleOpenGalaxy}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 18px",
              borderRadius: 20,
              background: "#0F766E",
              border: "none",
              color: "#F0FDFA",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              letterSpacing: "0.04em",
            }}
          >
            Open Galaxy
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 380,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Panel header */}
      <div
        style={{
          padding: "10px 14px 8px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#6B7280",
            }}
          >
            Flowchart — Account Path
          </div>
          {accountName && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "rgba(250,250,247,0.9)",
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 220,
              }}
            >
              {accountName}
            </div>
          )}
        </div>

        {/* Open full Galaxy link */}
        <button
          onClick={handleOpenGalaxy}
          title={`Open full Galaxy for ${accountName ?? accountId}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 10px",
            borderRadius: 14,
            background: "rgba(15,118,110,0.18)",
            border: "1px solid rgba(15,118,110,0.5)",
            color: "#5EEAD4",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <svg width={11} height={11} viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <path d="M1.5 9.5L9.5 1.5M9.5 1.5H4.5M9.5 1.5V6.5" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Open full Galaxy
        </button>
      </div>

      {/* Description card — per W66.X6 money-shot spec */}
      <GalaxyDescriptionCard />

      {/* FlowchartWall constrained embed */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #0a0a14 0%, #0d0d1a 50%, #1a1a2e 100%)",
          marginTop: 8,
        }}
      >
        {containerW > 0 && containerH > 0 && (
          <Suspense fallback={<GalaxyLoadingSpinner />}>
            {/* FlowchartWall fills the container — it is already full-bleed aware.
                We contain it inside a clipping div and use pointer-events:auto so
                the user can still navigate within the embedded galaxy (pan/zoom/floor).
                W45.3.1: selectedCp and onCpClick wired — clicking a Q-diamond
                broadcasts CP selection to Helix and Tower via useSelectedCp context. */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <FlowchartWall
                accountId={accountId}
                selectedCp={selectedCp}
                onCpClick={setSelectedCp}
              />
            </div>
          </Suspense>
        )}

        {/* W45.3/W45.3.1: CP selection badge overlay — shows which CP is currently
            selected in the cross-panel context. Positioned bottom-left so it
            does not obscure the Galaxy's existing floor-selector pill.
            Clicking the badge clears the selection.
            Diamond highlight wiring complete in W45.3.1 (FlowchartWall updated). */}
        {selectedCp !== null && (() => {
          const cp = HELIX_CHECKPOINTS[selectedCp - 1];
          return (
            <div
              style={{
                position: "absolute",
                bottom: 48,
                left: 10,
                zIndex: 30,
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 14,
                background: "rgba(29,78,216,0.22)",
                border: "1px solid #0B0B0F",
                boxShadow: "0 0 10px rgba(29,78,216,0.45)",
                color: "#93c5fd",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                fontFamily: "Inter, system-ui, sans-serif",
                pointerEvents: "auto",
                cursor: "pointer",
                userSelect: "none",
                transition: "opacity 0.2s ease",
              }}
              title={`Cross-panel: CP${selectedCp} selected. Click to clear.`}
              onClick={() => setSelectedCp(null)}
            >
              <span style={{ fontSize: 9, color: "#60a5fa", letterSpacing: "0.08em" }}>SELECTED</span>
              <span>CP{selectedCp}</span>
              {cp && (
                <span style={{ fontWeight: 500, color: "rgba(147,197,253,0.75)", fontSize: 10 }}>— {cp.name}</span>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
